
import { InboxState } from "@/utils/inbox/inbox-state";
import { InboxreaderPurchase } from "@/utils/purchase/inboxreader-purchase";
import { InboxreaderPurchaseFile } from "@/utils/purchase/inboxreader-purchase-file";
import Vue from "vue";
import AppTimeago from "@/components/common/Timeago.vue";
import { DossierConfigService } from "@/services";
import { printHumanFloat, printLocalDate } from "@/utils/pretty-print";
import { daysUntil, parseDayMonthYear } from "@/utils/inbox/dates";
import { InboxTypeState } from "@/utils/inbox/inbox-type-state";
import { selectionEntry, visibleFiles } from "@/utils/inbox/inbox-list-state";
import { InboxreaderService } from "@/services/inboxreader-service";
import { InboxreaderLabel } from "@/utils/purchase/inboxreader-label";
import { getLabelTextColor, labelItemHtml } from "@/utils/inbox/label-color";
import { ISelectItem } from "winfakt-vue-components";
import AppSetNoteForBookkeeperModal from "@/components/inbox/SetNoteForBookkeeperModal.vue";
import AppAssignLabelsModal from "@/components/inbox/AssignLabelsModal.vue";

export default Vue.extend({
	data(){
		return {
			setNoteForBookkeeperOpen: false,
			labelsFile: null as null | InboxreaderPurchaseFile
		};
	},
	props: {
		purchase: {
			type: Object as ()=>InboxreaderPurchase,
			required: true
		},
		state: {
			type: Object as ()=>InboxState,
			required: true
		},
		typeState: {
			type: Object as ()=>InboxTypeState,
			required: true
		},
		grid: Boolean,
	},
	computed: {
		isPeppol():boolean{
			return this.purchase.PeppolOrder != null;
		},
		files():InboxreaderPurchaseFile[]{
			return visibleFiles(this.purchase, this.state.archive);
		},
		// allFilesCount counts what the all files modal lists.
		allFilesCount():number{
			return this.purchase.Files.filter(f=>f.File != null).length;
		},
		hasMultipleFiles():boolean{
			return this.allFilesCount > 1;
		},
		requiresApproval():boolean{
			return DossierConfigService.getPurchaseConfig().RequireApprovalBeforeProcessing;
		},
		isSelfBilling():boolean{
			return this.purchase.PeppolOrder != null && this.purchase.PeppolOrder.isSelfBilling();
		},
		isUpload():boolean{
			return this.purchase.isUpload();
		},
		dash():string{
			return "—";
		},
		sourceKind():string{
			if (this.isPeppol) return "peppol";
			return this.isUpload ? "upload" : "email";
		},
		sourceIcon():string | string[]{
			if (this.isPeppol) return "share-alt";
			return this.isUpload ? "cloud-upload-alt" : ["far", "envelope"];
		},
		sourceTooltip():string{
			if (this.isPeppol) return this.$ct("source-peppol").toString();
			return this.$ct(this.isUpload ? "source-upload-tooltip" : "source-email-tooltip").toString();
		}
	},
	methods: {
		invoiceDateString(file:InboxreaderPurchaseFile):string{
			if (this.purchase.PeppolOrder) return printLocalDate(this.purchase.PeppolOrder.OrderDate);
			return file.getInvoiceDate();
		},
		// dueDate is the date the document has to be paid: out of the peppol
		// order, or out of what was read from the document.
		dueDate(file:InboxreaderPurchaseFile):Date | null{
			if (this.purchase.PeppolOrder) {
				let date = this.purchase.PeppolOrder.ExpiryDate;
				// A peppol order without a due date carries a zero date.
				if (!date || date.getFullYear() <= 1) return null;
				return date;
			}
			return parseDayMonthYear(file.getDueDate());
		},
		dueDateString(file:InboxreaderPurchaseFile):string{
			let date = this.dueDate(file);
			if (!date) return "";
			return printLocalDate(date);
		},
		// dueDateRelative counts the days that are left. A date that has passed
		// says nothing here: we do not know whether the invoice was paid.
		dueDateRelative(file:InboxreaderPurchaseFile):string{
			let days = daysUntil(this.dueDate(file));
			if (days == null || days < 0) return "";
			if (days == 0) return this.$ct("due-today").toString();
			return this.$ct("due-in-days", {count: days}).toString();
		},
		supplierName(file:InboxreaderPurchaseFile):string{
			if (this.purchase.PeppolOrder) return this.purchase.PeppolOrder.Supplier.Name;
			return file.getSupplierLabel() || this.purchase.From;
		},
		// documentRef is the line under the supplier: the number of the peppol
		// order, or the name of the file that came in.
		documentRef(file:InboxreaderPurchaseFile):string{
			if (this.purchase.PeppolOrder) return this.purchase.PeppolOrder.OrderNumber;
			return file.getName();
		},
		amountLabel(file:InboxreaderPurchaseFile):string{
			if (!this.purchase.PeppolOrder) return file.getTotalLabel();
			let order = this.purchase.PeppolOrder;
			let amount = printHumanFloat(order.TotalIncl);
			if (!order.Currency || order.Currency == "EUR") return `€ ${amount}`;
			return `${order.Currency} ${amount}`;
		},
		showApprovedIcon(file: InboxreaderPurchaseFile):boolean{
			if (!this.requiresApproval) return false;
			// A peppol document is approved once all of its files are
			if (this.isPeppol) {
				return this.files.every(f => f.Approved);
			}
			return file.Approved;
		},
		isEmailDisabled(file:InboxreaderPurchaseFile):boolean{
			if (!this.requiresApproval) return false;
			return !file.Approved;
		},
		// A shift click would also start selecting text in the browser.
		onMouseDown(event:MouseEvent){
			if (event.shiftKey) event.preventDefault();
		},
		onClick(file:InboxreaderPurchaseFile, event:MouseEvent){
			let entry = selectionEntry(this.purchase, file);
			if (event.shiftKey) {
				this.typeState.selectRange(entry);
			} else {
				this.typeState.selectItem(entry);
			}
		},
		isSelected(file:InboxreaderPurchaseFile):boolean{
			for (let p of this.typeState.selection){
				for (let f of p.Files) {
					if (f.ID == file.ID) return true;
				}
			}
			return false;
		},
		printLocalDate(date:Date):string{
			return printLocalDate(date);
		},
		async downloadFile(file:InboxreaderPurchaseFile){
			this.$emit("download", file);
		},
		previewFile(file:InboxreaderPurchaseFile){
			this.$emit("preview-file", file);
		},
		previewMail(purchase: InboxreaderPurchase){
			this.state.preview = purchase;
		},
		async archiveDocument(purchase:InboxreaderPurchase){
			let message = this.$ct("are-you-sure-you-want-to-archive-this-document");
			let confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			for (let file of purchase.Files) {
				await InboxreaderService.archiveInboxreaderPurchaseFile(purchase.ID, file.ID);
			}

			this.typeState.clearSelection();

			if(!purchase)
				return;

			/*purchase.Files = purchase.Files.filter(e => e.ID !== file.ID) ;
			if(purchase.Files.length == 0){
				await this.deletePurchase(purchase);
				return;
			}*/
			this.$emit("refresh-delete");
		},
		async unarchiveFile(file:InboxreaderPurchaseFile){
			if (this.isPeppol) {
				await InboxreaderService.unarchiveInboxreaderPurchase(this.purchase.ID);
			} else {
				await InboxreaderService.unarchiveInboxreaderPurchaseFile(this.purchase.ID, file.ID);
			}
			this.typeState.clearSelection();
			this.$emit("refresh-delete");
		},
		async deleteFile(file:InboxreaderPurchaseFile){
			let message = this.$ct("are-you-sure-you-want-to-delete-this-document");
			let confirm = await this.$wf.confirm(message, this.$ct("common.yes"), this.$ct("common.no"), this.$ct("common.warning"), "warning");
			if (!confirm) return;
			await InboxreaderService.deleteInboxreaderPurchaseFile(this.purchase.ID, file.ID);
			this.typeState.clearSelection();
			this.$emit("refresh-delete");
		},
		async deletePurchase(purchase:InboxreaderPurchase){
			await InboxreaderService.deleteInboxreaderPurchase(purchase.ID);
			this.$emit("refresh-delete");
		},
		sendMail(purchase: InboxreaderPurchase){
			this.$emit("send-mail", purchase);
		},
		openAllFiles(purchase:InboxreaderPurchase){
			this.$emit("open-all-files", purchase);
		},
		openAssignLabelsModal(file:InboxreaderPurchaseFile){
			this.labelsFile = file;
		},
		openSetNoteForBookkeeperModal(){
			this.setNoteForBookkeeperOpen = true;
		},
		labelItems(file:InboxreaderPurchaseFile):ISelectItem<number>[]{
			let assignedIds = file.Labels.map(l=>l.ID);
			return this.state.labels
				.filter(l=>assignedIds.indexOf(l.ID) == -1)
				.map(l=>({text: labelItemHtml(l), value: l.ID}));
		},
		async assignLabelToFile(file:InboxreaderPurchaseFile, labelId:number){
			let label = this.state.labels.find(l=>l.ID == labelId);
			if (!label) return;
			await InboxreaderService.assignLabel(labelId, [file.ID]);
			file.Labels.push(new InboxreaderLabel(label.getJSON()));
		},
		async removeLabelFromFile(file:InboxreaderPurchaseFile, label:InboxreaderLabel){
			await InboxreaderService.unassignLabel(label.ID, [file.ID]);
			let i = file.Labels.findIndex(l=>l.ID == label.ID);
			if (i != -1) file.Labels.splice(i, 1);
		},
		textColor(color:string):string{
			return getLabelTextColor(color);
		},
		// gridMenuItems is the overflow menu of a card. Archiving comes last and
		// is styled apart, so the order matters.
		gridMenuItems(file:InboxreaderPurchaseFile):ISelectItem<string>[]{
			let items:ISelectItem<string>[] = [
				{text: this.$ct("download").toString(), icon: "download", value: "download"},
				{text: this.$ct("add-label").toString(), icon: "tag", value: "labels"},
				{text: this.$ct("set-note-for-bookkeeper").toString(), icon: "sticky-note", value: "note"}
			];
			if (!this.isPeppol && !this.isUpload) {
				items.push({text: this.$ct("open-email").toString(), icon: "envelope", value: "open-mail", disabled: this.isEmailDisabled(file)});
			}
			items.push({text: this.$ct("forward-dots").toString(), icon: "share", value: "send-mail", disabled: this.isEmailDisabled(file)});
			if (this.hasMultipleFiles) {
				items.push({text: this.$ct("view-all-items", {count: this.allFilesCount}).toString(), icon: "copy", value: "all-files"});
			}
			items[items.length-1].devider = true;
			if (this.state.archive) {
				items.push({text: this.$ct("unarchive-document").toString(), icon: "undo", value: "unarchive"});
			} else {
				items.push({text: this.$ct("archive-document").toString(), icon: "archive", value: "archive"});
			}
			if (!this.isPeppol) {
				items.push({text: this.$ct("delete-document").toString(), icon: "trash", value: "delete"});
			}
			return items;
		},
		// The row has one menu: opening the document, then the menu of a card.
		rowMenuItems(file:InboxreaderPurchaseFile):ISelectItem<string>[]{
			return [
				{text: this.$ct("open-document").toString(), icon: "eye", value: "preview"},
				...this.gridMenuItems(file)
			];
		},
		onActionClick(file:InboxreaderPurchaseFile, action:string){
			let handlers:Record<string, ()=>void> = {
				preview: ()=>this.previewFile(file),
				download: ()=>this.downloadFile(file),
				note: ()=>this.openSetNoteForBookkeeperModal(),
				labels: ()=>this.openAssignLabelsModal(file),
				"send-mail": ()=>this.sendMail(this.purchase),
				"open-mail": ()=>this.previewMail(this.purchase),
				archive: ()=>this.archiveDocument(this.purchase),
				delete: ()=>this.deleteFile(file),
				unarchive: ()=>this.unarchiveFile(file),
				"all-files": ()=>this.openAllFiles(this.purchase)
			};
			let handler = handlers[action];
			if (handler) handler();
		}
	},
	components: {
		AppTimeago,
		AppSetNoteForBookkeeperModal,
		AppAssignLabelsModal
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	}
});
